import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getStoredUser } from "@/lib/api";
import { getIcon, LEVEL_COLORS } from "@/lib/agents";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowRight, Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", department: "", mission: "", system_prompt: "",
  autonomy_level: 1, color: "#00E5FF", icon: "Bot",
};

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const role = getStoredUser()?.role;
  const canManage = role === "owner" || role === "admin";

  const load = () =>
    api.get("/agents").then((r) => setAgents(r.data.agents)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditId(null); setForm(EMPTY); setDlg(true); };
  const openEdit = async (a) => {
    try {
      const { data } = await api.get(`/agents/${a.id}`); // trae system_prompt
      setEditId(a.id);
      setForm({
        name: data.name || "", department: data.department || "",
        mission: data.mission || "", system_prompt: data.system_prompt || "",
        autonomy_level: data.autonomy_level ?? 1, color: data.color || "#00E5FF",
        icon: data.icon || "Bot",
      });
      setDlg(true);
    } catch { toast.error("No se pudo cargar el agente"); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      if (editId) { await api.put(`/agents/${editId}`, form); toast.success("Agente actualizado"); }
      else { await api.post("/agents", form); toast.success("Agente creado"); }
      setDlg(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Quitar "${a.name}" de tu equipo?`)) return;
    try { await api.delete(`/agents/${a.id}`); toast.success("Agente quitado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo quitar"); }
  };

  if (loading) return <div className="p-8 text-[var(--text-dim)]" data-testid="agents-loading">Cargando…</div>;

  const active = agents.filter((a) => a.status === "active");
  // Agrupar por departamento
  const byDept = active.reduce((acc, a) => {
    (acc[a.department] = acc[a.department] || []).push(a);
    return acc;
  }, {});
  const depts = Object.keys(byDept).sort();

  return (
    <div className="p-8 space-y-8" data-testid="agents-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between gap-4">
        <div>
          <div className="label-mono mb-2">TU EQUIPO · {active.length} AGENTES</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Agentes</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Tu equipo de IA. Haz clic para chatear{canManage ? ", o crea y personaliza tus propios agentes." : "."}
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate}
                  className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80"
                  data-testid="agent-new">
            <Plus className="w-4 h-4 mr-2" /> Nuevo agente
          </Button>
        )}
      </div>

      {depts.map((dept) => (
        <div key={dept} className="space-y-3">
          <div className="label-mono text-[var(--text-dim)]">{dept}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {byDept[dept].map((a) => {
              const Icon = getIcon(a.icon);
              return (
                <div
                  key={a.id}
                  onClick={() => nav(`/agents/${a.id}`)}
                  className="cursor-pointer text-left border border-[var(--border)] bg-[var(--surface)] p-5 hover-neon group relative"
                  data-testid={`agent-card-${a.id}`}
                >
                  {canManage && (
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                              className="p-1.5 border border-[var(--border)] bg-[var(--bg)] text-[var(--text-dim)] hover:text-[var(--accent)]"
                              title="Editar" data-testid={`agent-edit-${a.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); remove(a); }}
                              className="p-1.5 border border-[var(--border)] bg-[var(--bg)] text-[var(--text-dim)] hover:text-[var(--danger)]"
                              title="Quitar" data-testid={`agent-del-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 border flex items-center justify-center"
                         style={{ borderColor: a.color, color: a.color }}>
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <Badge variant="outline"
                           className="rounded-none border-[var(--border)] font-mono text-[10px]"
                           style={{ color: LEVEL_COLORS[a.autonomy_level] }}>
                      L{a.autonomy_level}
                    </Badge>
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight mb-2 group-hover:text-[var(--accent)] transition-colors flex items-center gap-2">
                    {a.name}
                    {a.custom && <Sparkles className="w-3.5 h-3.5 text-[var(--accent)]" title="Agente propio" />}
                  </div>
                  <p className="text-xs text-[var(--text-dim)] leading-relaxed line-clamp-3">{a.mission}</p>
                  <div className="mt-4 flex items-center justify-end text-xs text-[var(--text-dim)]">
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Editar agente" : "Nuevo agente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label className="label-mono">Nombre</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                     placeholder="Ej. Community Manager"
                     className="rounded-none bg-[var(--bg)] border-[var(--border)]" data-testid="af-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-mono">Departamento</Label>
                <Input value={form.department} onChange={(e) => set("department", e.target.value)}
                       placeholder="Marketing" className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
              <div>
                <Label className="label-mono">Autonomía (0-5)</Label>
                <Input type="number" min="0" max="5" value={form.autonomy_level}
                       onChange={(e) => set("autonomy_level", parseInt(e.target.value) || 0)}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
            </div>
            <div>
              <Label className="label-mono">Misión (una frase)</Label>
              <Input value={form.mission} onChange={(e) => set("mission", e.target.value)}
                     placeholder="Qué hace este agente"
                     className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
            </div>
            <div>
              <Label className="label-mono">Instrucciones (system prompt)</Label>
              <Textarea value={form.system_prompt} onChange={(e) => set("system_prompt", e.target.value)}
                        rows={6} placeholder="Eres… Vigilas… Estructura tu respuesta… Responde en español."
                        className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid="af-prompt" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-mono">Color</Label>
                <Input value={form.color} onChange={(e) => set("color", e.target.value)}
                       placeholder="#00E5FF" className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" />
              </div>
              <div>
                <Label className="label-mono">Icono</Label>
                <Input value={form.icon} onChange={(e) => set("icon", e.target.value)}
                       placeholder="Bot, Star, Users…"
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving}
                    className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80"
                    data-testid="af-save">
              {saving ? "Guardando…" : editId ? "Guardar cambios" : "Crear agente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
